import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderComponent } from '../header/header.component';
import { AuthService } from '../auth.service';
import { slugify as translitSlug } from 'transliteration';
import { LanguageService } from '../language.service';
import { TranslationService } from '../translation.service';
import { PromotionService } from '../promotion.service';

@Component({
  selector: 'app-search-result',
  standalone: true,
  imports: [CommonModule, HeaderComponent],
  templateUrl: './search-result.component.html',
  styleUrl: './search-result.component.css'
})
export class SearchResultComponent {
  isLoggedIn: boolean = false;
  stories: any[] = [];
  categoryId: string | null = null;
  filteredSuggestions: any[] = [];
  isLoading = true;
  errorMessage = '';
  searchText: string = '';
  relatedStories: any[] = [];
  userProgress: any[] = [];
  categoryName: string = '';
  selectedLanguage: string = 'en';
  userId: number | null = null;
  displayCount: number = 1;
  visibleHistoryCount: number = 5;
  storyImgPath = 'https://stories.funtelstudio.com/storyapi';
sponsoredStories: any[] = [];
  showSponsoredInSearch = false;
  showSponsoredInCategory = false;
  constructor(
    private route: ActivatedRoute,
    public translationService: TranslationService,
    private languageService: LanguageService,
    private http: HttpClient, 
    private router: Router, 
    private authService: AuthService,
    private promotionService: PromotionService
  ) { }

  ngOnInit(): void {
    this.selectedLanguage = this.languageService.getCurrentLanguage();
    
    console.log('🔍 Search Result Init - Language:', this.selectedLanguage);
    
    this.languageService.currentLanguage$.subscribe(lang => {
      console.log('Language changed to:', lang);
      this.selectedLanguage = lang;
      this.refreshData();
    });

    const userId = this.authService.getCurrentUserId();
    if (userId) {
      this.userId = +userId;
    }

    // ✅ LOAD SPONSORED STORIES FIRST
    this.loadSponsoredStories().then(() => {
      this.route.paramMap.subscribe(params => {
        this.resetComponentState();
        this.categoryId = params.get('id');
        const searchText = this.route.snapshot.queryParamMap.get('q');
        
        console.log('📍 Route Parameters:', {
          categoryId: this.categoryId,
          searchText: searchText,
          queryParams: this.route.snapshot.queryParams,
          fullUrl: window.location.href
        });
        
        if (searchText) {
          this.fetchSearchResults(searchText);
          this.loadUserStoryProgress();
        } else if (this.categoryId) {
          this.fetchCategoryStories(this.categoryId);
          this.loadUserStoryProgress();
          this.loadCategoryName(this.categoryId);
        }
      });
    });
  }

  t(key: string): string {
    return this.translationService.translate(key);
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });
  }

  resetComponentState() {
    this.stories = [];
    this.relatedStories = [];
    this.categoryName = '';
    this.errorMessage = '';
    this.isLoading = true;
  }

 
  filterStoriesByLanguage(stories: any[]): any[] {
    if (!stories) return [];
    
    return stories.filter(story => {
      
      if (!story.language) return true;
      
     
      const storyLangCode = this.languageService.getCodeFromDBValue(story.language);
      return storyLangCode === this.selectedLanguage;
    });
  }


  refreshData() {
    const searchText = this.route.snapshot.queryParamMap.get('q');
    
    if (searchText) {
      this.fetchSearchResults(searchText);
    } else if (this.categoryId) {
      this.fetchCategoryStories(this.categoryId);
    }
  }

 loadCategoryName(categoryId: string) {
  console.log('🏷️ [CATEGORY NAME] Loading category name for:', categoryId);
  
  this.http.get<any>(`https://stories.funtelstudio.com/storyapi/category.php?id=${categoryId}`, {
    headers: this.getHeaders()
  }).subscribe({
    next: (res) => {
      console.log('✅ [CATEGORY NAME] category.php Response:', res);
      
      if (res.status === 'success' && res.data?.length > 0) {
        const match = res.data.find((cat: any) => cat.id.toString() === categoryId);
        this.categoryName = match?.categoryName || 'Category';
        console.log('🏷️ [CATEGORY NAME] Set to:', this.categoryName);
      }
    },
    error: (err) => {
      console.error('❌ [CATEGORY NAME] Failed to load category name', err);
    }
  });
}


  loadMoreHistory() {
    if (this.visibleHistoryCount < this.userProgress.length) {
      this.visibleHistoryCount += 5;
    }
  }
loadSponsoredStories(): Promise<void> {
    return new Promise((resolve) => {
      this.promotionService.getSponsoredStories().subscribe({
        next: (response) => {
          console.log('📢 Sponsored Stories for Search/Category:', response);
          
          if (response.status === 'success' && response.sponsored_stories.length > 0) {
            this.sponsoredStories = response.sponsored_stories.map(story => ({
              ...story,
              id: story.story_id, // ✅ Add regular id for compatibility
              isSponsored: true,
              sponsoredBadge: true
            }));
            
            console.log('✅ Sponsored stories loaded for search/category:', this.sponsoredStories.length);
          } else {
            console.log('ℹ️ No sponsored stories available for search/category');
            this.sponsoredStories = [];
          }
          resolve();
        },
        error: (error) => {
          console.error('❌ Error loading sponsored stories:', error);
          this.sponsoredStories = [];
          resolve();
        }
      });
    });
  }

  // ✅ ADD: Combine sponsored stories with regular stories
  combineWithSponsoredStories(regularStories: any[]): any[] {
    if (this.sponsoredStories.length === 0) {
      return regularStories;
    }

    console.log('🔄 Combining sponsored stories with regular stories');
    
    let combinedStories = [...regularStories];
    
    // Filter sponsored stories that match current context
    const relevantSponsoredStories = this.sponsoredStories.filter(sponsored => {
      // For category pages: check if sponsored story belongs to this category
      if (this.categoryId && sponsored.category === this.categoryId) {
        return true;
      }
      
      // For search pages: show random sponsored stories
      if (this.searchText) {
        return true; // Show sponsored stories in search results too
      }
      
      return false;
    });

    console.log('🎯 Relevant sponsored stories:', relevantSponsoredStories.length);

    if (relevantSponsoredStories.length > 0) {
      // Remove sponsored stories from regular stories to avoid duplicates
      const regularWithoutSponsored = combinedStories.filter(story => 
        !relevantSponsoredStories.some(sponsored => sponsored.story_id === story.id)
      );

      // Add sponsored stories at the beginning (max 3)
      const sponsoredToShow = relevantSponsoredStories.slice(0, 3);
      combinedStories = [...sponsoredToShow, ...regularWithoutSponsored];
      
      console.log('✅ Final combined stories:', {
        total: combinedStories.length,
        sponsored: sponsoredToShow.length,
        regular: regularWithoutSponsored.length
      });
    }

    return combinedStories;
  }
  fetchSearchResults(query: string) {
    this.searchText = query;
    this.isLoading = true;
    this.showSponsoredInSearch = true;
    this.showSponsoredInCategory = false;
    
    // Database ke liye language value use karein
    const dbLanguage = this.languageService.getCurrentLanguageForDB();
    const langParam = this.selectedLanguage !== 'en' ? `&language=${dbLanguage}` : '';
    
    this.http.get<any>(`https://stories.funtelstudio.com/storyapi/allstory.php?q=${encodeURIComponent(query)}${langParam}`, {
      headers: this.getHeaders()
    }).subscribe({
      next: (res) => {
        if (res.status === 'success') {
          // Language ke hisaab se filter karein
          let filteredStories = this.filterStoriesByLanguage(res.data);
          
          // ✅ COMBINE WITH SPONSORED STORIES
          this.stories = this.combineWithSponsoredStories(filteredStories);
          
          this.relatedStories = this.getRandomRelatedStories(this.stories, 3);
        } else {
          this.errorMessage = res.message || 'No matching stories found.';
          this.stories = [];
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.errorMessage = 'Failed to load search results.';
        this.isLoading = false;
        console.error('Search error:', err);
      }
    });
  }

  onSearchSubmit(event: Event) {
    event.preventDefault();
    const query = this.searchText.trim();
    if (query) {
      this.filteredSuggestions = [];
      this.router.navigate(['/search'], { queryParams: { q: query } });
    }
  }

fetchCategoryStories(categoryId: string) {
    this.isLoading = true;
    this.showSponsoredInCategory = true;
    this.showSponsoredInSearch = false;
    
    // Database ke liye language value use karein
    const dbLanguage = this.languageService.getCurrentLanguageForDB();
    const langParam = this.selectedLanguage !== 'en' ? `&language=${this.selectedLanguage}` : '';
    
    const apiUrl = `https://stories.funtelstudio.com/storyapi/allstory.php?category_id=${categoryId}${langParam}`;
    
    console.log('Fetching category stories:', {
      categoryId: categoryId,
      selectedLanguage: this.selectedLanguage,
      apiUrl: apiUrl
    });
    
    this.http.get<any>(apiUrl, {
      headers: this.getHeaders()
    }).subscribe({
      next: (res) => {
        console.log('Category API Response:', res);
        
        if (res.status === 'success') {
          let categoryStories = res.data; 
          
          // ✅ COMBINE WITH SPONSORED STORIES
          this.stories = this.combineWithSponsoredStories(categoryStories);
          
          console.log('Stories loaded with sponsored:', this.stories.length);
          
          this.relatedStories = this.getRandomRelatedStories(this.stories, 3);
        } else {
          this.errorMessage = res.message || 'No stories found.';
          this.stories = [];
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.errorMessage = 'Failed to load category stories.';
        this.isLoading = false;
        console.error('Category API Error:', err);
      }
    });
  }
  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? '' : date.toDateString();
  }

 goToSeries(storyId: string | number, title: string) {
    const slug = this.slugify(title);
    this.router.navigate(['/story', `${storyId}-${slug}`]);
    console.log('Navigating to story:', storyId, title, slug);
  }

  slugify(text: string): string {
    let slug = translitSlug(text, { lowercase: true, separator: '-' });
    if (!slug || slug.trim() === '') {
      slug = text.toLowerCase().replace(/\s+/g, '-');
    }
    return slug;
  }

  getRandomRelatedStories(allStories: any[], count: number): any[] {
    const shuffled = [...allStories].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  }

  resumeReading(storyId: number, title: string) {
    const slug = title.toLowerCase().replace(/\s+/g, '-');
    this.router.navigate(['/series', `${storyId}-${slug}`]);
  }
onStoryClick(story: any) {
    console.log('🎯 Story clicked - Search/Category:', {
      id: story.id,
      story_id: story.story_id,
      title: story.story_title,
      isSponsored: story.isSponsored,
      promotion_id: story.promotion_id,
      context: this.searchText ? 'Search' : 'Category'
    });
    
    // ✅ SPONSORED CLICK TRACKING
    if (story.isSponsored && story.promotion_id) {
      console.log('💰 Recording sponsored click from search/category:', story.promotion_id);
      this.recordSponsoredClick(story.promotion_id);
    }
    
    // ✅ FIX: Get the correct story ID for navigation
    const storyId = story.isSponsored ? story.story_id : story.id;
    const storyTitle = story.story_title;
    
    console.log('📍 Navigating to story:', { storyId, storyTitle, isSponsored: story.isSponsored });
    
    // Normal navigation
    setTimeout(() => {
      this.goToSeries(storyId.toString(), storyTitle);
    }, 50);
  }

  // ✅ ADD: Record sponsored click
  recordSponsoredClick(promotionId: number) {
    this.promotionService.recordInteraction(promotionId, 'click').subscribe({
      next: (response) => {
        console.log('✅ Click recorded from search/category:', response);
      },
      error: (error) => {
        console.error('❌ Error recording click:', error);
      }
    });
  }
  loadUserStoryProgress() {
    const userId = sessionStorage.getItem('userId');
    const key = `story_progress_${userId}`;
    const data = localStorage.getItem(key);

    if (data) {
      const progress = JSON.parse(data);
      const entries = Object.entries(progress);

      this.userProgress = [];

      for (const [storyId, entry] of Object.entries(progress) as [string, {
        lastReadPartId: number;
        readParts: number[];
        totalParts: number;
        lastPartTitle: string;
        timestamp: number;
        author_name: string;
      }][]) {
        const percent = Math.round((entry.readParts.length / entry.totalParts) * 100);
        const headers = new HttpHeaders({
          'Content-Type': 'application/json'
        });
        
        this.http.get<any>(`https://stories.funtelstudio.com/storyapi/allstory.php?id=${storyId}`, {
          headers: headers,
          withCredentials: true
        }).subscribe({
          next: (res) => {
            if (res.status === 'success' && res.data?.length) {
              const storyData = res.data.find((story: any) => story.id == +storyId);
              
              // ✅ Progress stories ko bhi language ke hisaab se filter karein
              if (this.shouldShowProgressStory(storyData)) {
                this.userProgress.push({
                  storyId,
                  title: entry.lastPartTitle,
                  lastReadPartId: entry.lastReadPartId,
                  percent,
                  part: entry.readParts.length,
                  total: entry.totalParts,
                  timestamp: entry.timestamp,
                  image: `${this.storyImgPath}/${storyData.story_image}`,
                  name: storyData.author_name,
                });

                this.userProgress.sort((a, b) => b.timestamp - a.timestamp);
              }
            }
          },
          error: (err) => {
            console.error('Error loading story progress:', err);
          }
        });
      }
    }
  }

  // ✅ Progress stories ko language ke hisaab se filter karein
  shouldShowProgressStory(storyData: any): boolean {
    if (!storyData.language) return true;
    
    const storyLangCode = this.languageService.getCodeFromDBValue(storyData.language);
    return storyLangCode === this.selectedLanguage;
  }

  clearHistory() {
    const userId = sessionStorage.getItem('userId');
    const key = `story_progress_${userId}`;
    localStorage.removeItem(key);
    location.reload();
  }

  formatCount(num: number | undefined | null): string {
    // Handle undefined, null, or invalid numbers
    if (num === undefined || num === null || isNaN(num)) {
        return '0';
    }
    
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M';
    if (num >= 1_000) return (num / 1_000).toFixed(1) + 'K';
    return num.toString();
}
}